import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { NumberInput } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { useAlias } from '@/lib/platform/hooks/use-alias';
import { AddEntryButton } from './AddEntryButton';
import { useBlankStart } from './use-blank-start';

interface SalesValueSectionProps {
  merchRevenue: number;
  matchdayRevenue: number;
  onMerchRevenueChange: (value: number) => void;
  onMatchdayRevenueChange: (value: number) => void;
  canEdit: boolean;
  numberFormat?: NumberFormatType;
}

export function SalesValueSection({
  merchRevenue,
  matchdayRevenue,
  onMerchRevenueChange,
  onMatchdayRevenueChange,
  canEdit,
  numberFormat = 'eu',
}: SalesValueSectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const { getLabel } = useAlias();
  // One shared blank-start: a single "+ Add" reveals both revenue fields
  const [expanded, expand] = useBlankStart(merchRevenue !== 0 || matchdayRevenue !== 0);

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          {getLabel('sales_value_title')}
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            HQ
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {getLabel('sales_value_description')}
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
        {/* Blank-start: one "+ Add" reveals both fields */}
        {!expanded && (
          <div className="sm:col-span-6">
            <AddEntryButton
              label={getLabel('sales_value_title')}
              onClick={expand}
              disabled={!canEdit}
            />
          </div>
        )}

        {expanded && (
          <>
            {/* Merchandise Revenue */}
            {/* data-ai-field tags match the AI form bridge field names */}
            <div className="sm:col-span-3" data-ai-field="merch_revenue">
              <NumberInput
                label={
                  <>
                    {getLabel('merchandise_revenue')}
                    <Tooltip {...tooltipData.merchandiseRevenue} wide={true} />
                  </>
                }
                suffix={getCurrencySymbol(baseCurrency)}
                value={merchRevenue}
                onChange={(value) => onMerchRevenueChange(typeof value === 'number' ? value : 0)}
                placeholder="0"
                disabled={!canEdit}
                numberFormat={numberFormat}
              />
            </div>

            {/* Matchday Revenue */}
            <div className="sm:col-span-3" data-ai-field="matchday_revenue">
              <NumberInput
                label={
                  <>
                    {getLabel('matchday_revenue')}
                    <Tooltip {...tooltipData.matchdayRevenue} wide={true} />
                  </>
                }
                suffix={getCurrencySymbol(baseCurrency)}
                value={matchdayRevenue}
                onChange={(value) => onMatchdayRevenueChange(typeof value === 'number' ? value : 0)}
                placeholder="0"
                disabled={!canEdit}
                numberFormat={numberFormat}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
